import { type GetApiGreetingsQueryParams } from '@/shared/api'
import { combine, createEvent, createStore, sample } from 'effector'
import { createGate } from 'effector-react'

import {
  greetingCreate,
  greetingDelete,
  greetingUpdate,
  greetingsStatus,
} from '../api'
import {
  type GreetingsQueryModelWithPagination,
  type GreetingsSortState,
  createGreetingsQueryModel,
} from '../lib/greetings-query-model'

interface GreetingsTableGateProps {
  initialFilters?: GetApiGreetingsQueryParams
  initialPage?: number
  initialPageSize?: number
  initialSearch?: string
  initialSort?: GreetingsSortState
}
export const GreetingsTableGate = createGate<GreetingsTableGateProps>()

export const GreetingsTableGreetingsQueryModel = createGreetingsQueryModel({
  usePagination: true,
  accumulate: false,
}) as GreetingsQueryModelWithPagination
const { availableSet, unavailableSet } = GreetingsTableGreetingsQueryModel
export const {
  //   Query
  query: greetingsTableQuery,
  //   Init
  $filters: $greetingsTableFilters,
  filtersInit: greetingsTableFiltersInit,
  filtersChanged: greetingsTableFiltersChanged,
  filtersReset: greetingsTableFiltersReset,
  //   Search
  $search: $greetingsTableSearch,
  searchChanged: greetingsTableSearchChanged,
  searchReset: greetingsTableSearchReset,
  //   Sort
  $sort: $greetingsTableSort,
  sortChanged: greetingsTableSortChanged,
  sortReset: greetingsTableSortReset,
  //   Pagination
  $page: $greetingsTablePage,
  pageChanged: greetingsTablePageChanged,
  pageReset: greetingsTablePageReset,
  $pageSize: $greetingsTablePageSize,
  pageSizeChanged: greetingsTablePageSizeChanged,
  //   Refresh
  refreshed: greetingsTableRefreshed,
} = GreetingsTableGreetingsQueryModel

sample({
  clock: GreetingsTableGate.open,
  target: availableSet,
})

// Search input

export const $greetingsTableSearchInputValue = createStore('')
  .on(greetingsTableSearchChanged, (_, q) => q)
  .reset(greetingsTableSearchReset)

// Setup initial params
// Page
sample({
  clock: GreetingsTableGate.open,
  source: combine(
    GreetingsTableGate.state,
    ({ initialPage }) => initialPage || null,
  ),
  filter: (page) => page !== null,
  target: greetingsTablePageChanged,
})
// Page Size
sample({
  clock: GreetingsTableGate.open,
  source: combine(
    GreetingsTableGate.state,
    ({ initialPageSize }) => initialPageSize || null,
  ),
  filter: (pageSize) => pageSize !== null,
  target: greetingsTablePageSizeChanged,
})
// Search
sample({
  clock: GreetingsTableGate.open,
  source: combine(
    GreetingsTableGate.state,
    ({ initialSearch }) => initialSearch || null,
  ),
  filter: (search) => search !== null,
  target: greetingsTableSearchChanged,
})
// Sort
sample({
  clock: GreetingsTableGate.open,
  source: combine(
    GreetingsTableGate.state,
    ({ initialSort }) => initialSort || null,
  ),
  filter: (sort) => sort !== null,
  target: greetingsTableSortChanged,
})
// Filters
sample({
  source: GreetingsTableGate.state,
  clock: availableSet,
  fn: ({ initialFilters: params }) => params || {},
  target: greetingsTableFiltersInit,
})

sample({
  clock: GreetingsTableGate.close,
  target: unavailableSet,
})

// Decrease page if there is only one record on page and it's being deleted
sample({
  source: combine({
    page: $greetingsTablePage,
    queryData: greetingsTableQuery.$data,
  }),
  clock: greetingDelete.finished.success,
  filter: ({ queryData, page }) =>
    !queryData?.items.length || (queryData?.items.length <= 1 && page > 1),
  fn: ({ page }) => page - 1,
  target: greetingsTablePageChanged,
})

sample({
  clock: [
    greetingCreate.finished.success,
    greetingUpdate.finished.success,
    greetingDelete.finished.success,
    greetingsStatus.finished.success,
  ],
  target: greetingsTableRefreshed,
})

// Selection
export const greetingIdsSelected = createEvent<string[]>()

export const $selectedGreetingIds = createStore<string[]>([])
  .on(greetingIdsSelected, (_, ids) => ids)
  .on(GreetingsTableGate.close, () => [])
